import { Injectable, Optional, Host } from '@angular/core';
import {
  addDays,
  addMonths,
  addQuarters,
  addWeeks,
  addYears,
  format,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
  subWeeks,
  subYears,
  isAfter,
  isSameDay,
} from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { DateRange, DynamicRange, Weekday, DateRangeValue, FixedDateRange, DynamicDateRange } from './types';
import { SessionService } from '../../services/session/session.service';

export class BaseDate {
  date: Date;
}

@Injectable()
export class DatesService {
  readonly base: Date;
  readonly validUnits = /^(day|week|month|quarter|year)s?$/;
  readonly accountFirstDayOfWeek: Weekday;

  constructor(@Host() @Optional() base: BaseDate, @Optional() sessionService: SessionService) {
    const options = sessionService?.account?.options;
    const timezone = (options?.timezone || 'eu') === 'eu' ? 'Europe/Stockholm' : 'America/New_York';
    this.base = base?.date || utcToZonedTime(new Date(), timezone);
    this.accountFirstDayOfWeek = (options && Number(options.first_day_of_week) === parseInt(options.first_day_of_week) && options.first_day_of_week >= 0) ? Number(options.first_day_of_week) % 7 : Weekday.Sunday;
  }

  public isDynamic(dateRange: DateRangeValue): dateRange is DynamicDateRange {
    return dateRange['dynamic'];
  }

  interval(dynamicRange: DynamicRange, queryFirstDayOfWeek?: Weekday): string {
    return this.fixedQueryInterval(this.parse(dynamicRange, queryFirstDayOfWeek));
  }

  public intervalDateRange(dateRange: DateRangeValue, queryFirstDayOfWeek?: Weekday): string {
    const date = this.parseValue(dateRange, queryFirstDayOfWeek);
    const startDate = format(date.startDate, 'yyyy-MM-dd');
    const endDate = format(date.endDate, 'yyyy-MM-dd');
    return `${startDate} - ${endDate}`;
  }

  fixedQueryInterval({ startDate, endDate }: DateRange): string {
    return `${format(startDate, 'yyyyMMdd')}-${format(endDate, 'yyyyMMdd')}`;
  }

  parseSingleDateValue(value: string): Date {
    const date = parseInt(value.replace(/-/g, ''), 10);
    return new Date(date / 10000, (date % 10000 / 100) - 1, date % 100);
  }

  parseValue(value: DateRangeValue, queryFirstDayOfWeek?: Weekday): DateRange {
    return this.isDynamic(value) ? this.parse(value['dynamic'], queryFirstDayOfWeek) : value;
  }

  parse(dynamicRange: DynamicRange, queryFirstDayOfWeek?: Weekday): DateRange {
    const base = new Date(this.base.getTime());
    switch (dynamicRange.name) {
      case 'today':
        return this.today(base);
      case 'yesterday':
        return this.yesterday(base);
      case 'last':
        return this.parseRelative(base, dynamicRange, 'last', queryFirstDayOfWeek);
      case 'this':
        return this.parseRelative(base, dynamicRange, 'this', queryFirstDayOfWeek);
      case 'next':
        return this.parseRelative(base, dynamicRange, 'next', queryFirstDayOfWeek);
    }
    throw new Error('Unable to parse dynamic date');
  }

  intervalToFixedDateRange(interval: string): FixedDateRange {
    const [startDate, endDate] = interval.split('-').map(dateStr => {
      const date = parseInt(dateStr, 10);
      return new Date(date / 10000, (date % 10000 / 100) - 1, date % 100);
    });
    return {
      type: 'fixed',
      startDate,
      endDate,
      range: 'Fixed',
    };
  }

  intervalToDateRange(interval: string, currentRange: DateRangeValue, queryFirstDayOfWeek?: Weekday): DateRangeValue {
    if (this.isDynamic(currentRange) && this.fixedQueryInterval(this.parseValue(currentRange, queryFirstDayOfWeek)) === interval) {
      return currentRange;
    }

    return this.intervalToFixedDateRange(interval);
  }

  public dateRangeToFixedDateRange(dates: DateRange): FixedDateRange {
    return {
      type: 'fixed',
      startDate: dates.startDate,
      endDate: dates.endDate,
      range: 'Fixed',
    };
  }

  public isValid(startDate: Date, endDate: Date): boolean {
    return isAfter(endDate, startDate) || isSameDay(endDate, startDate);
  }

  private parseRelative(d: Date, dynamicRange: DynamicRange, mode: 'last' | 'this' | 'next', queryFirstDayOfWeek?: Weekday): DateRange {
    let cursor = new Date(d.getTime());
    let unit = dynamicRange.unit;
    let num = dynamicRange.num || 1;
    const today = new Date(d.getTime());

    if (unit) {
      const matches = unit.match(this.validUnits);
      if (matches) {
        unit = <any> matches[1];
        if (num) {
          let endDate: Date;
          let startDate: Date;

          if (unit === 'week') {
            cursor = startOfDay(startOfWeek(cursor, { weekStartsOn: queryFirstDayOfWeek ?? this.accountFirstDayOfWeek }));
            const numDays = 7 * num;
            if (mode !== 'last') {
              cursor = addDays(cursor, 7);
            }
            if (mode === 'next') {
              startDate = new Date(cursor.getTime());
              endDate = subDays(addDays(cursor, numDays), 1);
            } else {
              endDate = subDays(cursor, 1);
              startDate = subDays(cursor, numDays);
              if (mode === 'this' && dynamicRange.toDate && this.supportsToDate(false, unit)) {
                endDate = dynamicRange.offset === -1 ? subDays(today, 1) : today;
              }
            }
          } else {
            cursor = this.startOfUnit(cursor, unit);
            if (mode !== 'last') {
              cursor = this.addUnit(cursor, unit, 1);
            }
            if (mode === 'next') {
              startDate = new Date(cursor.getTime());
              endDate = subDays(this.addUnit(cursor, unit, num), 1);
            } else {
              endDate = subDays(cursor, 1);
              startDate = this.addUnit(cursor, unit, -num);

              if (mode === 'this' && dynamicRange.toDate && this.supportsToDate(false, unit)) {
                endDate = dynamicRange.offset === -1 ? subDays(today, 1) : today;
              }
            }
          }

          return this.toDateRange(startDate, endDate);
        }
      }
    }
    throw new Error('Unable to parse dynamic date');
  }

  private startOfUnit(date: Date, unit: string): Date {
    switch (unit) {
      case 'day': return startOfDay(date);
      case 'month': return startOfMonth(date);
      case 'quarter': return startOfQuarter(date);
      case 'year': return startOfYear(date);
      default: return date;
    }
  }

  private addUnit(date: Date, unit: string, amount: number): Date {
    switch (unit) {
      case 'day': return amount >= 0 ? addDays(date, amount) : subDays(date, -amount);
      case 'week': return amount >= 0 ? addWeeks(date, amount) : subWeeks(date, -amount);
      case 'month': return amount >= 0 ? addMonths(date, amount) : subMonths(date, -amount);
      case 'quarter': return amount >= 0 ? addQuarters(date, amount) : subQuarters(date, -amount);
      case 'year': return amount >= 0 ? addYears(date, amount) : subYears(date, -amount);
      default: return date;
    }
  }

  private supportsToDate(last: boolean, unit: string): boolean {
    if (!last && (unit === 'month' || unit === 'quarter' || unit === 'year')) {
      return true;
    } else {
      return false;
    }
  }

  private yesterday(d: Date): DateRange {
    const s = subDays(d, 1);
    return this.toDateRange(s, s);
  }

  private today(d: Date): DateRange {
    const s = new Date(d.getTime());
    return this.toDateRange(s, s);
  }

  private toDateRange(s: Date, e: Date): DateRange {
    return { startDate: s, endDate: e };
  }
}
